/**
 * Funnel step: choose a visual style — AFTER the realistic master
 * generation, using the customer's OWN dog rendered in each style, never
 * generic example imagery. Two styles only (realistic + animated_3d) —
 * christmas/halloween were removed from the product. Reads
 * generationResult { realisticUrl, animated3dUrl } from the shared funnel
 * state (funnel-state.js), written by generation-engine.js's polling.
 *
 * realisticUrl is guaranteed present (it's what unlocked this page). The
 * other card may still be generating server-side — it shows a skeleton
 * and a read-only polling loop (GET /api/tripo/status, no credits) fills
 * it in live, never blocking the page or the other card. A card can only
 * be selected once its own image has loaded.
 *
 * On "Continuer": saves { selectedStyle } and calls
 * GET /api/tripo/status?task_id=...&selected_style=<style> ONCE — the
 * mechanism the backend uses to start the side view for ONLY the chosen
 * style. Real response fields: selected_style, side_task_id, side_status,
 * side_view_url, side_error, front_view_url (never "multiview_*" — an
 * earlier wrong guess, corrected after inspecting a real response). This
 * NEVER calls /api/tripo/generate again.
 */
(async () => {
  const grid = document.querySelector('[data-style-picker-grid]');
  if (!grid) return;

  const config = window.PfGenerationConfig || { API_BASE: '', DEBUG: false };
  function log(...args) {
    if (config.DEBUG) console.info('[PRENDFORM]', ...args);
  }

  // Test mode (?test=1): renders with a local mock result when no real
  // generation exists yet, so the page can be reviewed without running the
  // funnel or spending Kie.ai credits. No sessionStorage write, no network
  // call — purely local. Ignored entirely once a real result exists.
  const isTestMode = new URLSearchParams(window.location.search).get('test') === '1';

  let state = window.PfFunnel ? window.PfFunnel.get() : {};
  let realResult = state.generationStatus === 'completed' ? state.generationResult : null;
  const usingMock = !(realResult && realResult.realisticUrl) && isTestMode;

  // Fase 3B: local state (PfFunnel/localStorage) can be entirely empty
  // here - a different device, a browser that lost its cache, or simply a
  // very old visit - while the backend already has everything durably
  // persisted (Fase 3A's R2 previews + D1 selected_style). Before giving
  // up and showing "nothing to choose from yet", try recovering from the
  // backend once. This AWAITS (unlike the background reconciliation
  // further down) only because there is nothing useful to paint yet
  // either way - it never delays a page that already has something to
  // show locally.
  if (!usingMock && (!realResult || !realResult.realisticUrl) && state.designId && window.PfGeneration && window.PfGeneration.recoverDesign) {
    const recovered = await window.PfGeneration.recoverDesign(state.designId);
    if (recovered) {
      state = window.PfFunnel.get();
      realResult = state.generationStatus === 'completed' ? state.generationResult : null;
    }
  }

  const gridResult = usingMock
    ? {
        realisticUrl: window.PfMockFigureUrl,
        animated3dUrl: window.PfMockFigureUrl,
      }
    : realResult;

  const continueBtn = document.querySelector('[data-style-picker-continue]');
  const errorEl = document.querySelector('[data-style-picker-error]');

  // The realistic master must exist — it's what this page is FOR. If it
  // doesn't, this is genuinely "nothing to choose from yet" (even after
  // trying to recover it from the backend above), not a transient loading
  // state — show the same error+retour pattern used elsewhere in the
  // funnel rather than silently redirecting.
  if (!gridResult || !gridResult.realisticUrl) {
    grid.hidden = true;
    if (continueBtn) continueBtn.hidden = true;
    if (errorEl) errorEl.hidden = false;
    return;
  }

  const cards = Array.from(grid.querySelectorAll('[data-style-card]'));
  const continueLabel = document.querySelector('[data-style-picker-continue-label]');
  const nextUrl = grid.dataset.nextUrl || '/pages/tu-figurita';

  const FIELD_TO_BACKEND = {
    realisticUrl: 'realistic_url',
    animated3dUrl: 'animated_3d_url',
  };

  let selectedStyle = null;

  function applyImage(card, url) {
    const img = card.querySelector('[data-style-card-img]');
    const skeleton = card.querySelector('[data-style-card-skeleton]');
    if (url && img) {
      img.src = url;
      img.hidden = false;
    }
    if (skeleton) skeleton.hidden = !!url;
    card.disabled = !url;
  }

  function refreshCardsFromResult(res) {
    cards.forEach((card) => applyImage(card, res[card.dataset.styleField]));
  }

  refreshCardsFromResult(gridResult);

  function applySelection() {
    cards.forEach((card) => {
      const isSelected = card.dataset.styleCard === selectedStyle;
      card.classList.toggle('is-selected', isSelected);
      card.setAttribute('aria-checked', String(isSelected));
    });
    if (continueBtn) continueBtn.disabled = !selectedStyle;
  }

  cards.forEach((card) => {
    card.addEventListener('click', () => {
      if (card.disabled) return;
      selectedStyle = card.dataset.styleCard;
      applySelection();
    });
  });

  // Realistic is always ready by the time this page loads — pre-select it
  // so the customer never has to think, while still being free to change.
  selectedStyle = 'realistic';
  applySelection();

  // Fase 3B: reconcile with the backend in the background. Never blocks
  // the initial paint above - this only UPGRADES it if the backend knows
  // more than local state does (a style already chosen on another
  // visit/device, or a preview persisted in R2 surviving Kie.ai's own
  // ~24h URL expiry). Backend/D1 is the source of truth for
  // selected_style once a designId exists; PfFunnel/localStorage is only
  // a cache. Never touches masterTaskId/animated3dTaskId/sideTaskId/
  // base* fields - out of scope for this phase.
  if (!usingMock && state.designId && window.PfGeneration && window.PfGeneration.recoverDesign) {
    window.PfGeneration.recoverDesign(state.designId).then((recovered) => {
      if (!recovered) return;

      if (recovered.generationResult) {
        refreshCardsFromResult(recovered.generationResult);
      }

      // Only re-apply if the backend actually disagrees with what's
      // already selected, and only once that style's own card has an
      // image to select (never select a still-loading/disabled card).
      if (recovered.selectedStyle && recovered.selectedStyle !== selectedStyle) {
        const targetCard = cards.find((c) => c.dataset.styleCard === recovered.selectedStyle);
        if (targetCard && !targetCard.disabled) {
          log('Backend selected_style differs from local pre-selection - backend wins:', recovered.selectedStyle);
          selectedStyle = recovered.selectedStyle;
          applySelection();
        }
      }
    });
  }

  continueBtn?.addEventListener('click', async () => {
    if (!selectedStyle) return;

    continueBtn.disabled = true;
    if (continueLabel) continueLabel.textContent = 'Un instant…';

    if (usingMock) {
      window.location.href = nextUrl + '?test=1';
      return;
    }

    window.PfFunnel.set({ selectedStyle });

    // Fase 3 (contrato explícito de selección): confirms the choice was
    // durably persisted in D1 before treating it as settled - never
    // silently assumed. Non-blocking on failure, same philosophy as the
    // side-view trigger right below: syncSelectedStyle() itself already
    // records selectedStyleConfirmed:false on any failure (network error,
    // non-2xx, or a response missing persisted:true), so a future
    // recoverDesign() call (this page or a later one) can detect and
    // reconcile it - the customer is never trapped here by a backend
    // hiccup.
    if (window.PfGeneration && window.PfGeneration.syncSelectedStyle) {
      await window.PfGeneration.syncSelectedStyle(selectedStyle);
    }

    const taskId = state.masterTaskId || state.generationJobId;
    if (taskId) {
      try {
        const url = `${config.API_BASE}/api/tripo/status?task_id=${encodeURIComponent(taskId)}&selected_style=${encodeURIComponent(selectedStyle)}`;
        log('Triggering side view for selected style:', selectedStyle);
        const res = await fetch(url);
        if (res.ok) {
          const data = await res.json();
          // Real backend contract: side_task_id/side_status/side_view_url/
          // side_error (never "multiview_*" — that was a wrong guess from
          // before this field-name mismatch was found via a real test).
          window.PfFunnel.set({
            sideTaskId: data.side_task_id || taskId,
            sideStatus: data.side_status || 'queued',
          });
          if (data.front_view_url || data.side_view_url) {
            const current = window.PfFunnel.get().generationResult || {};
            window.PfFunnel.set({
              generationResult: Object.assign({}, current, {
                frontViewUrl: data.front_view_url || current.frontViewUrl || null,
                sideViewUrl: data.side_view_url || current.sideViewUrl || null,
              }),
            });
          }
          log('Side view trigger response:', data);
        } else {
          log('Side view trigger request failed with status', res.status, '— proceeding to tu-figurita anyway with the chosen style image.');
        }
      } catch (e) {
        log('Multiview trigger request errored — proceeding to tu-figurita anyway:', e);
      }
    }

    window.location.href = nextUrl;
  });

  // --- Fill in any style variants that weren't ready yet when this page
  // loaded (read-only polling, no credits, no /generate call) ------------
  const taskId = state.masterTaskId || state.generationJobId;
  const missingFields = cards.map((c) => c.dataset.styleField).filter((field) => !gridResult[field]);
  if (usingMock || missingFields.length === 0 || !taskId) return;

  const POLL_INTERVAL_MS = 2500;
  const POLL_RETRY_BACKOFF_MS = [2000, 3000, 5000];
  let pollTimer = null;
  let pollFailureCount = 0;

  function stopPolling() {
    if (pollTimer) clearTimeout(pollTimer);
    pollTimer = null;
  }

  async function pollVariants() {
    try {
      // Recovers animated_3d_task_id from PfFunnel on EVERY poll (not a
      // local variable) - this is what makes it survive arriving at this
      // page fresh after a refresh/navigation, and echoing it back is what
      // stops the backend from creating a brand new animated_3d task
      // (its in-memory cache is only a best-effort safety net, never
      // guaranteed across serverless instances/cold starts). Same field,
      // same behavior as generation-engine.js's pollOnce().
      const knownAnimated3dTaskId = (window.PfFunnel && window.PfFunnel.get().animated3dTaskId) || null;
      let url = `${config.API_BASE}/api/tripo/status?task_id=${encodeURIComponent(taskId)}`;
      if (knownAnimated3dTaskId) {
        url += `&animated_3d_task_id=${encodeURIComponent(knownAnimated3dTaskId)}`;
      }
      const res = await fetch(url);
      if (!res.ok) throw new Error('status_failed');
      const data = await res.json();
      pollFailureCount = 0;

      if (data.animated_3d_task_id) {
        window.PfFunnel?.set({ animated3dTaskId: data.animated_3d_task_id });
      }

      const current = (window.PfFunnel && window.PfFunnel.get().generationResult) || {};
      const updated = Object.assign({}, current);
      let changed = false;
      Object.keys(FIELD_TO_BACKEND).forEach((key) => {
        if (!updated[key] && data[FIELD_TO_BACKEND[key]]) {
          updated[key] = data[FIELD_TO_BACKEND[key]];
          changed = true;
        }
      });

      if (changed) {
        window.PfFunnel.set({ generationResult: updated });
        refreshCardsFromResult(updated);
        log('Style variant(s) arrived:', updated);
      }

      const stillMissing = Object.keys(FIELD_TO_BACKEND).some((key) => !updated[key]);
      // styles_ready is an array of style keys already done (e.g.
      // ["realistic","animated_3d",...]), not a boolean.
      const allReadyPerBackend = Array.isArray(data.styles_ready) && data.styles_ready.length >= Object.keys(FIELD_TO_BACKEND).length;
      if (!stillMissing || allReadyPerBackend) {
        stopPolling();
        return;
      }
      pollTimer = setTimeout(pollVariants, POLL_INTERVAL_MS);
    } catch (e) {
      pollFailureCount += 1;
      if (pollFailureCount > POLL_RETRY_BACKOFF_MS.length) {
        // Give up quietly — whichever cards already loaded stay selectable,
        // the rest just keep their skeleton. Never shows a page-level error.
        stopPolling();
        return;
      }
      pollTimer = setTimeout(pollVariants, POLL_RETRY_BACKOFF_MS[pollFailureCount - 1]);
    }
  }

  pollVariants();
})();
